import { useEffect, useRef } from "react";

/**
 * Re-run `fn` on an interval, but only while the tab is actually being looked at.
 *
 * WHY THIS EXISTS. Most screens fetched once on mount and never again, so a
 * technician watching the ticket queue on a live helpdesk saw nothing new
 * until they reloaded the page — reported as "AeroGauge does not auto
 * refresh". The queue is the one screen whose whole job is to tell you
 * something arrived.
 *
 * WHY IT STOPS WHEN HIDDEN. The screens that *did* poll did so unconditionally,
 * so every forgotten background tab kept hitting the API for ever. This
 * appliance exhausted its database connection pool twice in nine days; idle
 * tabs are load it does not need to carry. A hidden tab is, by definition,
 * nobody waiting for an answer.
 *
 * WHY IT FIRES ON RETURN. Pausing alone would mean coming back to a stale
 * screen and waiting out the remainder of the interval — which looks exactly
 * like the bug being fixed. Becoming visible refetches immediately.
 *
 * `fn` is held in a ref so callers can pass an inline closure without the
 * interval being torn down and rebuilt on every render.
 */
export function useAutoRefresh(fn: () => void, intervalMs: number, enabled = true): void {
  const saved = useRef(fn);
  useEffect(() => {
    saved.current = fn;
  }, [fn]);

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;

    const run = () => {
      if (document.visibilityState === "visible") saved.current();
    };

    const id = setInterval(run, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") saved.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs, enabled]);
}
